/**
 * Offline-safe queue for lead submissions.
 * If a submission fails (network error, CORS, etc.), store it in localStorage
 * and retry automatically on the next page load.
 * This guarantees we NEVER lose a customer, even on total fetch failure.
 */

import { robustPost } from './robustFetch';

const QUEUE_KEY = 'ale_pending_leads';

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch {
    /* localStorage full or disabled — ignore */
  }
}

/**
 * Add a failed submission to the retry queue.
 */
export function queueLead(payload) {
  const queue = readQueue();
  queue.push({
    id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    payload,
    queued_at: new Date().toISOString(),
    attempts: 0,
  });
  writeQueue(queue);
}

/**
 * Try to flush all pending leads. Called on app load and after successful submissions.
 * Silent: never throws, just logs.
 */
export async function flushLeadQueue() {
  const queue = readQueue();
  if (queue.length === 0) return;

  const stillPending = [];
  for (const item of queue) {
    if (item.attempts >= 10) {
      // Give up after 10 attempts to avoid infinite retry
      continue;
    }
    try {
      const response = await robustPost('/api/lead', item.payload);
      if (!response.ok && response.status !== 0) {
        // Validation error (4xx): drop — won't succeed on retry
        if (response.status >= 400 && response.status < 500) {
          continue;
        }
        // Server error: keep for later
        stillPending.push({ ...item, attempts: item.attempts + 1 });
      }
      // 2xx or duplicate: drop from queue
    } catch {
      // Network error: keep for later
      stillPending.push({ ...item, attempts: item.attempts + 1 });
    }
  }
  writeQueue(stillPending);
}
